import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, Save, Trash2, X, UserPlus, Copy, Check, Pencil } from 'lucide-react';
import {
  AdminClientApi,
  InviteDetailInterface,
  ClientCouponInterface,
  ClientUserInterface,
  InviteAllocationWithUserInterface,
} from '../../Admin-ClientLibrary';
import { getApiUrl } from '../../config/api';
import {
  editInviteSchema,
  EditInviteFormData,
  assignInviteSchema,
  AssignInviteFormData,
  EditAllocationFormData,
} from '../../schemas/invite.schema';
import './InviteDetail.css';

const getToken = (): string => {
  return localStorage.getItem('authToken') || '';
};

const adminApi = new AdminClientApi(getApiUrl(), getToken);

const InviteDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [invite, setInvite] = useState<InviteDetailInterface | null>(null);
  const [coupons, setCoupons] = useState<ClientCouponInterface[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [wasUpdated, setWasUpdated] = useState<boolean>(false);

  // React Hook Form for Edit Invite
  const editInviteForm = useForm<EditInviteFormData>({
    resolver: zodResolver(editInviteSchema),
    defaultValues: {
      name: '',
      description: '',
      coupon_id: undefined,
      is_active: true,
    },
  });

  const [showAssignModal, setShowAssignModal] = useState<boolean>(false);
  const [assignLoading, setAssignLoading] = useState<boolean>(false);
  const [userSearch, setUserSearch] = useState<string>('');
  const [userResults, setUserResults] = useState<ClientUserInterface[]>([]);
  const [selectedUser, setSelectedUser] = useState<ClientUserInterface | null>(null);

  // React Hook Form for Assign Invite
  const assignInviteForm = useForm<AssignInviteFormData>({
    resolver: zodResolver(assignInviteSchema),
    defaultValues: {
      user_id: undefined,
      quantity: 1,
      notes: '',
    },
  });

  const [generatingUrl, setGeneratingUrl] = useState<number | null>(null);
  const [copiedAllocationId, setCopiedAllocationId] = useState<number | null>(null);

  // Edit allocation modal state
  const [showEditAllocationModal, setShowEditAllocationModal] = useState<boolean>(false);
  const [editingAllocation, setEditingAllocation] = useState<InviteAllocationWithUserInterface | null>(null);
  const [editAllocationLoading, setEditAllocationLoading] = useState<boolean>(false);

  // React Hook Form for Edit Allocation (created dynamically based on quantity_used)
  const editAllocationForm = useForm<EditAllocationFormData>({
    defaultValues: {
      quantity_allocated: 1,
      notes: '',
    },
  });

  const resetModalState = (): void => {
    setSelectedUser(null);
    setUserSearch('');
    setUserResults([]);
    assignInviteForm.reset({
      user_id: undefined,
      quantity: 1,
      notes: '',
    });
  };

  const closeAssignModal = (): void => {
    setShowAssignModal(false);
    resetModalState();
  };

  const openEditAllocationModal = (allocation: InviteAllocationWithUserInterface): void => {
    setEditingAllocation(allocation);
    // Update form with dynamic resolver based on quantity_used
    editAllocationForm.reset({
      quantity_allocated: allocation.quantity_allocated,
      notes: allocation.notes || '',
    });
    setShowEditAllocationModal(true);
  };

  const closeEditAllocationModal = (): void => {
    setShowEditAllocationModal(false);
    setEditingAllocation(null);
    editAllocationForm.reset({
      quantity_allocated: 1,
      notes: '',
    });
  };

  useEffect(() => {
    fetchCoupons();
    if (id) {
      fetchInvite(parseInt(id));
    }
  }, [id]);

  // Reset edit invite form when invite data is loaded
  useEffect(() => {
    if (invite) {
      editInviteForm.reset({
        name: invite.name,
        description: invite.description || '',
        coupon_id: invite.coupon_id,
        is_active: invite.is_active,
      });
    }
  }, [invite]);

  useEffect(() => {
    // Debounce user search by 1.5 seconds
    const debounceTimer = setTimeout(() => {
      if (userSearch.length >= 2) {
        searchUsers(userSearch);
      } else {
        setUserResults([]);
      }
    }, 1500);

    // Cleanup function to cancel pending searches
    return () => {
      clearTimeout(debounceTimer);
    };
  }, [userSearch]);

  const fetchCoupons = async (): Promise<void> => {
    try {
      const response = await adminApi.getCoupons({ limit: 100, is_active: true });
      setCoupons(response.coupons);
    } catch (err: any) {
      console.error('Error fetching coupons:', err);
    }
  };

  const fetchInvite = async (inviteId: number, showLoading: boolean = true): Promise<void> => {
    try {
      if (showLoading) {
        setLoading(true);
      }
      setError(null);

      const response = await adminApi.getInvite({ id: inviteId }, false);

      if (response.success && response.invite) {
        setInvite(response.invite);
      } else {
        setError(response.message || 'Invite not found');
      }
    } catch (err: any) {
      console.error('Error fetching invite:', err);
      setError(err.message || 'Failed to fetch invite');
    } finally {
      if (showLoading) {
        setLoading(false);
      }
    }
  };

  const searchUsers = async (search: string): Promise<void> => {
    try {
      const response = await adminApi.getUsers({ search, limit: 10 });
      setUserResults(response.users);
    } catch (err: any) {
      console.error('Error searching users:', err);
    }
  };

  const onSaveInvite = async (data: EditInviteFormData): Promise<void> => {
    if (!invite) return;

    try {
      setSaving(true);

      const response = await adminApi.updateInvite({
        id: invite.id,
        name: data.name !== invite.name ? data.name : undefined,
        description: data.description !== (invite.description || '') ? (data.description || undefined) : undefined,
        coupon_id: data.coupon_id !== invite.coupon_id ? (data.coupon_id || null) : undefined,
        is_active: data.is_active !== invite.is_active ? data.is_active : undefined,
      });

      if (response.success && response.invite) {
        await fetchInvite(invite.id, false);
        setWasUpdated(true);
      } else {
        alert(response.message || 'Failed to update invite');
      }
    } catch (err: any) {
      console.error('Error updating invite:', err);
      alert(err.message || 'Failed to update invite');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (): Promise<void> => {
    if (!invite) return;

    if (!window.confirm(`Are you sure you want to delete "${invite.name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const response = await adminApi.deleteInvite({ id: invite.id });

      if (response.success) {
        navigate('/invites', { state: { refresh: true } });
      } else {
        alert(response.message || 'Failed to delete invite');
      }
    } catch (err: any) {
      console.error('Error deleting invite:', err);
      alert(err.message || 'Failed to delete invite');
    }
  };

  const onAssignInvite = async (data: AssignInviteFormData): Promise<void> => {
    if (!invite || !selectedUser) return;

    try {
      setAssignLoading(true);

      const response = await adminApi.assignInviteToUser({
        invite_id: invite.id,
        user_id: selectedUser.account_id,
        quantity: data.quantity,
        notes: data.notes || undefined,
      });

      if (response.success) {
        closeAssignModal();
        fetchInvite(invite.id, false);
      } else {
        alert(response.message || 'Failed to assign invite');
      }
    } catch (err: any) {
      console.error('Error assigning invite:', err);
      alert(err.message || 'Failed to assign invite');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleDeleteAllocation = async (inviteId: number, allocationId: number): Promise<void> => {
    if (!window.confirm('Are you sure you want to remove this allocation?')) {
      return;
    }

    try {
      const response = await adminApi.deleteUserInviteAllocation({ inviteId: inviteId, allocationId: allocationId });

      if (response.success) {
        if (invite) {
          fetchInvite(invite.id, false);
        }
      } else {
        alert(response.message || 'Failed to delete allocation');
      }
    } catch (err: any) {
      console.error('Error deleting allocation:', err);
      alert(err.message || 'Failed to delete allocation');
    }
  };

  const onUpdateAllocation = async (data: EditAllocationFormData): Promise<void> => {
    if (!invite || !editingAllocation) return;

    // Validate that the new quantity is at least equal to quantity_used
    if (data.quantity_allocated < editingAllocation.quantity_used) {
      editAllocationForm.setError('quantity_allocated', {
        type: 'manual',
        message: `Quantity allocated cannot be less than quantity already used (${editingAllocation.quantity_used})`,
      });
      return;
    }

    try {
      setEditAllocationLoading(true);

      const response = await adminApi.updateUserInviteAllocation({
        id: editingAllocation.id,
        quantity_allocated: data.quantity_allocated,
        notes: data.notes || undefined,
      });

      if (response.success) {
        closeEditAllocationModal();
        fetchInvite(invite.id, false);
      } else {
        alert(response.message || 'Failed to update allocation');
      }
    } catch (err: any) {
      console.error('Error updating allocation:', err);
      alert(err.message || 'Failed to update allocation');
    } finally {
      setEditAllocationLoading(false);
    }
  };

  const handleGenerateAndCopyUrl = async (inviteId: number, allocationId: number): Promise<void> => {
    setGeneratingUrl(allocationId);
    setCopiedAllocationId(null);

    try {
      const response = await adminApi.generateInviteUrl({ inviteId: inviteId, allocationId: allocationId });

      if (response.success && response.inviteUrl) {
        await navigator.clipboard.writeText(response.inviteUrl);
        setCopiedAllocationId(allocationId);

        // Reset copied state after 2 seconds
        setTimeout(() => {
          setCopiedAllocationId(null);
        }, 2000);
      } else {
        alert(response.message || 'Failed to generate invite URL');
      }
    } catch (err: any) {
      console.error('Error generating invite URL:', err);
      alert(err.message || 'Failed to generate invite URL');
    } finally {
      setGeneratingUrl(null);
    }
  };

  const formatDiscount = (): string => {
    if (!invite?.coupon) return 'No coupon linked';
    if (invite.coupon.percent_off) {
      return `${invite.coupon.percent_off}% off`;
    }
    if (invite.coupon.amount_off) {
      return `$${(invite.coupon.amount_off / 100).toFixed(2)} off`;
    }
    return invite.coupon.name;
  };

  const formatDate = (date: Date | undefined): string => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handleBack = (): void => {
    if (wasUpdated) {
      navigate('/invites', { state: { refresh: true } });
    } else {
      navigate('/invites');
    }
  };

  if (loading) {
    return (
      <div className="invite-detail-page">
        <div className="loading">Loading invite...</div>
      </div>
    );
  }

  if (error || !invite) {
    return (
      <div className="invite-detail-page">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Invites
        </button>
        <div className="error-message">
          <p>{error || 'Invite not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invite-detail-page">
      <div className="detail-header">
        <button className="back-button" onClick={handleBack}>
          <ArrowLeft size={16} />
          Back to Invites
        </button>

        <div className="header-content">
          <div className="invite-title">
            <h1>{invite.name}</h1>
            {invite.description && (
              <span className="invite-description">{invite.description}</span>
            )}
          </div>
          <div className="header-actions">
            <button
              className="btn-primary"
              onClick={editInviteForm.handleSubmit(onSaveInvite)}
              disabled={!editInviteForm.formState.isDirty || saving}
            >
              <Save size={16} />
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
            <button className="btn-danger" onClick={handleDelete}>
              <Trash2 size={16} />
              Delete
            </button>
          </div>
        </div>
      </div>

      <div className="detail-content">
        <div className="detail-card">
          <h2>Invite Details</h2>
          <div className="info-grid">
            <div className="info-item">
              <span className="info-label">Linked Coupon</span>
              <div className={`coupon-highlight ${!invite.coupon ? 'no-coupon' : ''}`}>
                {formatDiscount()}
              </div>
            </div>
            <div className="info-item">
              <span className="info-label">Status</span>
              <span className={`status-badge ${invite.is_active ? 'active' : 'inactive'}`}>
                {invite.is_active ? 'Active' : 'Inactive'}
              </span>
            </div>
            <div className="info-item">
              <span className="info-label">Total Allocations</span>
              <span className="info-value large">{invite.allocations.length}</span>
            </div>
            <div className="info-item">
              <span className="info-label">Created</span>
              <span className="info-value">{formatDate(invite.createdAt)}</span>
            </div>
          </div>
        </div>

        <div className="detail-card">
          <h2>Edit Invite</h2>
          <form className="edit-form" onSubmit={editInviteForm.handleSubmit(onSaveInvite)}>
            <div className="form-group">
              <label>Invite Name *</label>
              <input
                type="text"
                {...editInviteForm.register('name')}
                placeholder="Enter invite name"
                className={editInviteForm.formState.errors.name ? 'input-error' : ''}
              />
              {editInviteForm.formState.errors.name && (
                <span className="form-error">{editInviteForm.formState.errors.name.message}</span>
              )}
            </div>

            <div className="form-group">
              <label>Description</label>
              <textarea
                {...editInviteForm.register('description')}
                placeholder="Optional description"
              />
            </div>

            <div className="form-group">
              <label>Linked Coupon (optional)</label>
              <Controller
                name="coupon_id"
                control={editInviteForm.control}
                render={({ field }) => (
                  <select
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                    disabled={!!invite?.coupon_id}
                  >
                    <option value="">No coupon</option>
                    {coupons.map((coupon) => (
                      <option key={coupon.id} value={coupon.id}>
                        {coupon.name} ({coupon.percent_off ? `${coupon.percent_off}%` : `$${((coupon.amount_off || 0) / 100).toFixed(2)}`} off)
                      </option>
                    ))}
                  </select>
                )}
              />
              <span className="form-hint">
                If selected, the coupon will be applied when this invite is used.
              </span>
            </div>

            <div className="form-group">
              <label>Status</label>
              <div className="toggle-switch">
                <input
                  type="checkbox"
                  {...editInviteForm.register('is_active')}
                />
                <span className="toggle-label">
                  {editInviteForm.watch('is_active') ? 'Active' : 'Inactive'}
                </span>
              </div>
              <span className="form-hint">
                Deactivating an invite will prevent it from being used.
              </span>
            </div>

            {editInviteForm.formState.isDirty && (
              <div className="form-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    if (invite) {
                      editInviteForm.reset({
                        name: invite.name,
                        description: invite.description || '',
                        coupon_id: invite.coupon_id,
                        is_active: invite.is_active,
                      });
                    }
                  }}
                >
                  Discard Changes
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                >
                  <Save size={16} />
                  {saving ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            )}
          </form>
        </div>

        <div className="detail-card">
          <div className="card-header">
            <h2>User Allocations</h2>
            <button className="btn-primary" onClick={() => setShowAssignModal(true)}>
              <UserPlus size={16} />
              Assign to User
            </button>
          </div>

          {invite.allocations.length > 0 ? (
            <div className="allocations-table-container">
              <table className="allocations-table">
                <thead>
                  <tr>
                    <th>User</th>
                    <th>Allocated</th>
                    <th>Used</th>
                    <th>Remaining</th>
                    <th>Notes</th>
                    <th>Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invite.allocations.map((allocation: InviteAllocationWithUserInterface) => (
                    <tr key={allocation.id}>
                      <td>
                        <strong>{allocation.user.name}</strong>
                        <br />
                        <small style={{ color: 'var(--text-secondary)' }}>
                          {allocation.user.email}
                        </small>
                      </td>
                      <td>{allocation.quantity_allocated}</td>
                      <td>{allocation.quantity_used}</td>
                      <td>
                        <span style={{ color: allocation.quantity_remaining > 0 ? '#16a34a' : '#dc2626' }}>
                          {allocation.quantity_remaining}
                        </span>
                      </td>
                      <td>{allocation.notes || '-'}</td>
                      <td>{formatDate(allocation.createdAt)}</td>
                      <td>
                        <div className="allocation-actions">
                          <button
                            className="action-btn"
                            onClick={() => openEditAllocationModal(allocation)}
                            title="Edit allocation"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            className="action-btn primary"
                            onClick={() => handleGenerateAndCopyUrl(invite.id, allocation.id)}
                            title="Copy Invite URL"
                            disabled={generatingUrl === allocation.id || allocation.quantity_remaining <= 0}
                          >
                            {copiedAllocationId === allocation.id ? (
                              <Check size={14} />
                            ) : (
                              <Copy size={14} />
                            )}
                          </button>
                          <button
                            className="action-btn danger"
                            onClick={() => handleDeleteAllocation(invite.id, allocation.id)}
                            title="Remove allocation"
                            disabled={allocation.quantity_used > 0}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-allocations">
              <p>No users have been assigned this invite yet.</p>
              <button className="btn-secondary" onClick={() => setShowAssignModal(true)}>
                <UserPlus size={16} />
                Assign First User
              </button>
            </div>
          )}
        </div>
      </div>

      {showAssignModal && (
        <div className="modal-overlay" onClick={closeAssignModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Assign Invite to User</h2>
              <button className="modal-close" onClick={closeAssignModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={assignInviteForm.handleSubmit(onAssignInvite)}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Search User *</label>
                  <div className="user-search-container">
                    <input
                      type="text"
                      value={userSearch}
                      onChange={(e) => {
                        setUserSearch(e.target.value);
                        setSelectedUser(null);
                        assignInviteForm.setValue('user_id', undefined as any);
                      }}
                      placeholder="Search by name or email..."
                      disabled={!!selectedUser}
                    />
                    {userResults.length > 0 && !selectedUser && (
                      <div className="user-results">
                        {userResults.map((user) => (
                          <div
                            key={user.account_id}
                            className="user-result-item"
                            onClick={() => {
                              setSelectedUser(user);
                              assignInviteForm.setValue('user_id', user.account_id);
                              setUserResults([]);
                            }}
                          >
                            <div className="user-result-name">{user.name}</div>
                            <div className="user-result-email">{user.email}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {selectedUser && (
                    <div className="selected-user">
                      <div className="selected-user-info">
                        <span className="selected-user-name">{selectedUser.name}</span>
                        <span className="selected-user-email">{selectedUser.email}</span>
                      </div>
                      <button
                        type="button"
                        className="remove-user-btn"
                        onClick={() => {
                          setSelectedUser(null);
                          setUserSearch('');
                          assignInviteForm.setValue('user_id', undefined as any);
                        }}
                      >
                        <X size={16} />
                      </button>
                    </div>
                  )}
                  {assignInviteForm.formState.errors.user_id && (
                    <span className="form-error">{assignInviteForm.formState.errors.user_id.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Quantity *</label>
                  <Controller
                    name="quantity"
                    control={assignInviteForm.control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min="1"
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                        className={assignInviteForm.formState.errors.quantity ? 'input-error' : ''}
                      />
                    )}
                  />
                  <span className="form-hint">Number of times the user can use this invite</span>
                  {assignInviteForm.formState.errors.quantity && (
                    <span className="form-error">{assignInviteForm.formState.errors.quantity.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    {...assignInviteForm.register('notes')}
                    placeholder="Optional notes about this allocation"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeAssignModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={assignLoading || !selectedUser}
                >
                  {assignLoading ? 'Assigning...' : 'Assign Invite'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showEditAllocationModal && editingAllocation && (
        <div className="modal-overlay" onClick={closeEditAllocationModal}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Edit Allocation</h2>
              <button className="modal-close" onClick={closeEditAllocationModal}>
                <X size={20} />
              </button>
            </div>
            <form onSubmit={editAllocationForm.handleSubmit(onUpdateAllocation)}>
              <div className="modal-body">
                <div className="selected-user" style={{ marginBottom: '16px' }}>
                  <div className="selected-user-info">
                    <span className="selected-user-name">{editingAllocation.user.name}</span>
                    <span className="selected-user-email">{editingAllocation.user.email}</span>
                  </div>
                </div>

                <div className="allocation-stats" style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ flex: 1, padding: '12px', background: 'var(--background, #f9fafb)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Used</div>
                    <div style={{ fontSize: '18px', fontWeight: '600' }}>{editingAllocation.quantity_used}</div>
                  </div>
                  <div style={{ flex: 1, padding: '12px', background: 'var(--background, #f9fafb)', borderRadius: '8px' }}>
                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginBottom: '4px' }}>Remaining</div>
                    <div style={{ fontSize: '18px', fontWeight: '600', color: (editAllocationForm.watch('quantity_allocated') || 0) - editingAllocation.quantity_used > 0 ? '#16a34a' : '#dc2626' }}>
                      {(editAllocationForm.watch('quantity_allocated') || 0) - editingAllocation.quantity_used}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label>Quantity Allocated *</label>
                  <Controller
                    name="quantity_allocated"
                    control={editAllocationForm.control}
                    render={({ field }) => (
                      <input
                        type="number"
                        min={editingAllocation.quantity_used}
                        value={field.value}
                        onChange={(e) => field.onChange(parseInt(e.target.value) || editingAllocation.quantity_used)}
                        className={editAllocationForm.formState.errors.quantity_allocated ? 'input-error' : ''}
                      />
                    )}
                  />
                  <span className="form-hint">
                    Minimum: {editingAllocation.quantity_used} (already used)
                  </span>
                  {editAllocationForm.formState.errors.quantity_allocated && (
                    <span className="form-error">{editAllocationForm.formState.errors.quantity_allocated.message}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Notes</label>
                  <textarea
                    {...editAllocationForm.register('notes')}
                    placeholder="Optional notes about this allocation"
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeEditAllocationModal}>
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={editAllocationLoading || (editAllocationForm.watch('quantity_allocated') || 0) < editingAllocation.quantity_used}
                >
                  {editAllocationLoading ? 'Saving...' : 'Save Changes'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InviteDetail;

